import firebase from "firebase/app";
import "firebase/auth";
import { Store } from "redux";

import { RootState } from "../store/types";
import {
    loadUserByFirebaseID,
    getUserFeatures,
    listenToUser,
    UserData,
    makeOwnerFromDocument,
    getUserFlags,
    DocumentData,
} from "common/Database";
import { sleep, assert } from "common/Support";
import { setUser, updateUser } from "../reducers/user-data";
import { setOrganization, removeOrganization, setOrganizationsLoaded } from "../reducers/organizations";
import { logInfo, logError } from "common/debug-print";
import { unloadNonSampleApps, updateAppsWithUserFeatures } from "../actions/actions";
import { onAuthStateChanged, setAuthPersistence, signOut } from "./firebase-auth";
import { resetUndo } from "./undo";
import { getUnaffiliatedDatabase, getBuilderUserDatabase } from "./web-database";
import { identifyUser } from "../analytics";
import { OwnerKind } from "common/Database/owner-kind";
import { usersCollectionName } from "common/database-strings";
import * as actions from "../actions/actions";
import * as userActions from "../actions/user-actions";
import { handleTermsOfServiceUpdate } from "designer/lib/terms-of-service";
import { dispatchThunk } from "../actions/action-thunk";
import { setTrackingConsent, unsetTrackingConsent } from "common/tracking-consent";
import { saveUserUpdates } from "./firebase-functions";
import { isPlayer } from "routes";

// We need to set the user's email in profitwell when the auth data changes.
declare const profitwell: (action: "user_email", email: string) => void;

export function loadOrCreateUser(reduxStore: Store<RootState>): void {
    let userUnlisten: (() => void) | undefined;
    const orgUnlistens = new Map<string, () => void>();

    function unlistenIfNecessary(): void {
        if (userUnlisten !== undefined) {
            userUnlisten();
            userUnlisten = undefined;
        }
        for (const unlisten of orgUnlistens.values()) {
            unlisten();
        }
        orgUnlistens.clear();
    }

    async function setFirebaseUser(firebaseUser: firebase.User): Promise<UserData | undefined> {
        unlistenIfNecessary();

        logInfo("setting firebase user", firebaseUser);
        const { uid } = firebaseUser;
        let user: UserData | undefined;
        // We do this because for some reason Firebase gives us a permission error
        // here sometimes, even tough it just told us that we have an authenticated
        // user.
        for (;;) {
            try {
                user = await loadUserByFirebaseID(getUnaffiliatedDatabase(), uid);
            } catch (e: unknown) {
                logError("loading user failed", e);
                await sleep(100);
                continue;
            }
            break;
        }
        logInfo("user", user);
        if (user === undefined) {
            // This shouldn't, but could, happen. Particularly: if we didn't manage to finish
            // the sign up process. Since the sign up process happens in a "critical section"
            // where the callbacks are disabled, this is safe.
            await signOut();
            return undefined;
        }

        // There's a race condition on signout that can leave the app
        // that was open at the time of signout in the "loading" state,
        // which will lead to it not to load when signed in again.
        // This clears the state of all the apps to unloaded.
        dispatchThunk(reduxStore, unloadNonSampleApps(user.appIDs));
        reduxStore.dispatch(setUser(user));
        dispatchThunk(reduxStore, updateAppsWithUserFeatures(getUserFeatures(user)));
        resetUndo();

        const db = getBuilderUserDatabase(user.id);

        async function loadOrgs(orgIDs: ReadonlySet<string>, retry: boolean) {
            const orgsLoaded = new Set<string>();
            for (const outer of orgIDs) {
                try {
                    const orgID = outer;
                    assert(!orgUnlistens.has(orgID));
                    const unlisten = db.listenToDocument(
                        usersCollectionName,
                        orgID,
                        (data: DocumentData) => {
                            const org = makeOwnerFromDocument(db, data, orgID);
                            if (org.kind !== OwnerKind.Organization) {
                                logError("Organization is not an organization", orgID);
                                return;
                            }
                            reduxStore.dispatch(setOrganization(org));
                            orgsLoaded.add(orgID);

                            if (!retry && orgsLoaded.size === orgIDs.size) {
                                reduxStore.dispatch(setOrganizationsLoaded());
                            }
                        },
                        async e => {
                            logError("Error listening to org", orgID, e);
                            orgUnlistens.delete(orgID);
                            await sleep(2000);
                            await loadOrgs(new Set([orgID]), true);
                        }
                    );
                    orgUnlistens.set(orgID, unlisten);
                } catch (e: unknown) {
                    logError("Could not listen to org", outer, e);
                    if (!retry) {
                        // We might need to back off as the backend function
                        // may not have set the permissions correctly by the time
                        // we get this update.
                        await sleep(2000);
                        await loadOrgs(new Set([outer]), true);
                    }
                }
            }
        }

        userUnlisten = listenToUser(db, user.id, async u => {
            const oldOrgs = reduxStore.getState().organizations;
            reduxStore.dispatch(updateUser(u));

            const newOrgIds = u.orgUserIDs.filter(id => oldOrgs.find(o => o.id === id) === undefined);
            const removedOrgs = oldOrgs.filter(p => u.orgUserIDs.find(o => o === p.id) === undefined);

            if (newOrgIds.length === 0) {
                reduxStore.dispatch(setOrganizationsLoaded());
            } else {
                await loadOrgs(new Set(newOrgIds), false);
            }

            for (const { id } of removedOrgs) {
                const unlisten = orgUnlistens.get(id);
                if (unlisten !== undefined) {
                    unlisten();
                    orgUnlistens.delete(id);
                }
                reduxStore.dispatch(removeOrganization(id));
            }
        });

        return user;
    }

    reduxStore.dispatch(setUser("loading"));

    onAuthStateChanged(async firebaseUser => {
        logInfo("auth state changed", firebaseUser);
        if (firebaseUser === undefined) {
            unlistenIfNecessary();
            reduxStore.dispatch(setUser(null));
            unsetTrackingConsent();
        } else {
            const glideUser = await setFirebaseUser(firebaseUser);
            if (glideUser !== undefined) {
                const { builderTrackingOptOut, onboardingComplete, lastAcceptedTermsOfService } = getUserFlags(
                    glideUser
                );
                setTrackingConsent(!builderTrackingOptOut);
                await setAuthPersistence(!builderTrackingOptOut);

                // saveUserUpdates will create the hubspot contact
                if (glideUser.hubspotContactID === undefined) {
                    await saveUserUpdates({}, {});
                }

                // We are married to Firebase Auth for analytics purposes.
                // A long time ago, we started feeding Firebase Auth UIDs into
                // Intercom, and now Intercom is dead certain that those are the
                // unique IDs for users. Moving away from this requires us to
                // reconcile all the differences in the incredibly slow Intercom API.
                // Now that we have removed Intercom, we are still stuck with this
                // for legacy purposes
                await identifyUser(firebaseUser.uid, {
                    name: firebaseUser.displayName ?? undefined,
                    email: firebaseUser.email ?? undefined,
                    avatar: firebaseUser.photoURL ?? undefined,
                    allowEmail: glideUser.flags?.allowEmail,
                });

                if (typeof firebaseUser.email === "string" && !isPlayer()) {
                    profitwell("user_email", firebaseUser.email);
                }

                const newUser = !onboardingComplete || lastAcceptedTermsOfService === 0;
                if (!newUser) {
                    // Onboarding and new team invite handles the first update
                    // of lastAcceptedTermsOfService
                    await handleTermsOfServiceUpdate(
                        glideUser,
                        async v => await dispatchThunk(reduxStore, userActions.setLastAcceptedTermsOfService(v)),
                        async () => await dispatchThunk(reduxStore, actions.signOut())
                    );
                }
            }
        }
    });
}
